<template>
    <div class="date-picker-container" tabindex="0" @keydown.enter="onEnter">
        <div class="date-picker-header">
            <button class="nav-button" @click="handleNavigation('prev')">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="chevron-left"
                >
                    <path d="M15 18l-6-6 6-6" />
                </svg>
            </button>
            <div class="month-year-selector">
                <span @click="setView('months')" class="clickable">{{ currentMonthName }}</span>
                <span @click="setView('years')" class="clickable">{{ currentYear }}</span>
            </div>
            <button class="nav-button" @click="handleNavigation('next')">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="chevron-right"
                >
                    <path d="M9 18l6-6-6-6" />
                </svg>
            </button>
        </div>

        <div class="date-display-row" v-if="currentView === 'days'">
            <div class="selected-date">
                {{ formatSelectedDate }}
            </div>
            <button class="today-button" @click="goToToday">Today</button>
        </div>

        <!-- Days View -->
        <div class="calendar" v-if="currentView === 'days'">
            <div class="weekdays">
                <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
            </div>
            <div class="days">
                <div
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    :class="[
                        'day',
                        { 'current-month': day.currentMonth },
                        { 'other-month': !day.currentMonth },
                        { selected: isSelected(day.date) },
                        { 'has-event': day.hasEvent },
                    ]"
                    @click="selectDate(day.date)"
                >
                    {{ day.day }}
                    <div v-if="day.hasEvent" class="event-dot"></div>
                </div>
            </div>
        </div>

        <!-- Months View -->
        <div class="months-grid" v-if="currentView === 'months'">
            <div
                v-for="(month, index) in monthNames"
                :key="month"
                :class="['month-item', { selected: currentMonth === index }]"
                @click="selectMonth(index)"
            >
                {{ month.substring(0, 3) }}
            </div>
        </div>

        <!-- Years View -->
        <div class="years-grid" v-if="currentView === 'years'">
            <div
                v-for="year in yearRange"
                :key="year"
                :class="['year-item', { selected: currentYear === year }]"
                @click="selectYear(year)"
            >
                {{ year }}
            </div>
        </div>

        <div class="actions" v-if="currentView === 'days'">
            <PrimeButton label="Cancel" @click="cancel" variant="secondary" class="p-btn-root_secondary w-full" />
            <PrimeButton label="Apply" @click="apply" variant="primary" class="w-full" :loading="isApplying" />
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';
import { computed, onMounted, ref } from 'vue';

// Props and emits
const props = defineProps({
    initialDate: {
        type: Date,
        default: () => new Date(),
    },
    isApplying: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['cancel', 'apply']);

// State
const currentDate = ref(new Date(props.initialDate || new Date()));
const selectedDate = ref(new Date(props.initialDate || new Date()));
const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const currentView = ref('days'); // 'days', 'months', 'years'
const yearRangeStart = ref(2020);

// Computed properties
const currentYear = computed(() => currentDate.value.getFullYear());
const currentMonth = computed(() => currentDate.value.getMonth());
const currentMonthName = computed(() => {
    return monthNames[currentMonth.value];
});

const formatSelectedDate = computed(() => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(selectedDate.value);
});

const yearRange = computed(() => {
    const years = [];
    for (let i = 0; i < 12; i++) {
        years.push(yearRangeStart.value + i);
    }
    return years;
});

const calendarDays = computed(() => {
    const year = currentYear.value;
    const month = currentMonth.value;

    // First day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDay = new Date(year, month, 1).getDay();
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0).getDate();
    // Last day of previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    // Adjust for Monday as first day of week
    const firstDayAdjusted = firstDay === 0 ? 6 : firstDay - 1;

    const days = [];

    // Previous month days
    for (let i = firstDayAdjusted - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevMonthLastDay - i);
        days.push({
            day: prevMonthLastDay - i,
            currentMonth: false,
            date,
            hasEvent: false,
        });
    }

    // Current month days
    for (let i = 1; i <= lastDay; i++) {
        const date = new Date(year, month, i);
        // Add event dots to first and last day of the month
        const hasEvent = i === 1 || i === lastDay;
        days.push({
            day: i,
            currentMonth: true,
            date,
            hasEvent,
        });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        days.push({
            day: i,
            currentMonth: false,
            date,
            hasEvent: false,
        });
    }

    return days;
});

// Methods
function handleNavigation(direction: 'prev' | 'next') {
    if (currentView.value === 'days') {
        if (direction === 'prev') {
            previousMonth();
        } else {
            nextMonth();
        }
    } else if (currentView.value === 'months') {
        if (direction === 'prev') {
            currentDate.value = new Date(currentYear.value - 1, currentMonth.value, 1);
        } else {
            currentDate.value = new Date(currentYear.value + 1, currentMonth.value, 1);
        }
    } else if (currentView.value === 'years') {
        if (direction === 'prev') {
            yearRangeStart.value -= 12;
        } else {
            yearRangeStart.value += 12;
        }
    }
}

function previousMonth() {
    currentDate.value = new Date(currentYear.value, currentMonth.value - 1, 1);
}

function nextMonth() {
    currentDate.value = new Date(currentYear.value, currentMonth.value + 1, 1);
}

function selectDate(date: Date) {
    // Create a new date at noon to avoid timezone issues
    selectedDate.value = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
}

function isSelected(date: Date) {
    return (
        date.getDate() === selectedDate.value.getDate() &&
        date.getMonth() === selectedDate.value.getMonth() &&
        date.getFullYear() === selectedDate.value.getFullYear()
    );
}

function goToToday() {
    const today = new Date();
    // Set to noon to avoid timezone issues
    const todayAtNoon = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0);
    currentDate.value = todayAtNoon;
    selectedDate.value = todayAtNoon;
    currentView.value = 'days';
}

function setView(view: 'days' | 'months' | 'years') {
    currentView.value = view;
    if (view === 'years') {
        // Center the year range around the current year
        yearRangeStart.value = Math.floor(currentYear.value / 12) * 12;
    }
}

function selectMonth(monthIndex: number) {
    currentDate.value = new Date(currentYear.value, monthIndex, 1);
    currentView.value = 'days';
}

function selectYear(year: number) {
    currentDate.value = new Date(year, currentMonth.value, 1);
    currentView.value = 'months';
}

function cancel() {
    emit('cancel');
}

function apply() {
    emit('apply', selectedDate.value);
}

const onEnter = (e: KeyboardEvent) => {
    if (currentView.value === 'days' && !props.isApplying) {
        apply();
    }
};

// Initialize with January 2025 and select January 10th as shown in the design
onMounted(() => {
    if (props.initialDate) {
        currentDate.value = new Date(props.initialDate);
        selectedDate.value = new Date(props.initialDate);
    } else {
        currentDate.value = new Date(2025, 0, 1); // January 2025
        selectedDate.value = new Date(2025, 0, 10); // January 10, 2025
    }
});
</script>

<style scoped>
.date-picker-container {
    @apply sm:w-[360px] w-11/12 mx-auto bg-white rounded-lg shadow-lg p-4;
}

.date-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.month-year-selector {
    font-size: 20px;
    font-weight: 500;

    display: flex;
    gap: 8px;
    color: var(--Gray-700, #414651);
    text-align: center;

    /* Text md/Semibold */
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 150% */
}

.clickable {
    cursor: pointer;
}

.clickable:hover {
    color: #0078ff;
}

.nav-button {
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.nav-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.date-display-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
}

.selected-date {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 16px;
    color: #333;
    overflow: hidden;
    color: var(--Gray-900, #181d27);
    text-overflow: ellipsis;

    /* Text md/Regular */
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 150% */
}

.today-button {
    margin-left: 16px;
    padding: 10px 14px;
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    color: var(--Gray-700, #414651);

    /* Text sm/Semibold */
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px; /* 142.857% */
    cursor: pointer;
}

.today-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.calendar {
    margin-bottom: 20px;
}

.weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    margin-bottom: 8px;
}

.weekday {
    font-size: 14px;
    color: #666;
    padding: 8px 0;
    color: var(--Gray-700, #414651);
    text-align: center;

    /* Text sm/Medium */
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
}

.days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
}

.day {
    position: relative;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 50%;
    cursor: pointer;
    color: var(--Gray-700, #414651);
    text-align: center;

    /* Text sm/Regular */
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
}

.current-month {
    color: #333;
}

.other-month {
    color: #bbb;
}

.selected {
    background-color: #0078ff;
    color: white;
}

.day:hover:not(.selected) {
    background-color: rgba(0, 0, 0, 0.05);
}

.event-dot {
    position: absolute;
    bottom: 4px;
    width: 4px;
    height: 4px;
    background-color: #0078ff;
    border-radius: 50%;
}

/* Months View */
.months-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 16px 0;
}

.month-item {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 8px;
    cursor: pointer;
}

.month-item:hover:not(.selected) {
    background-color: rgba(0, 0, 0, 0.05);
}

/* Years View */
.years-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 16px 0;
}

.year-item {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 8px;
    cursor: pointer;
}

.year-item:hover:not(.selected) {
    background-color: rgba(0, 0, 0, 0.05);
}

.actions {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    border-top: 1px solid #eee;
    padding-top: 20px;
    gap: 12px;
}

.cancel-button,
.apply-button {
    padding: 12px 0;
    width: 48%;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    text-align: center;
}

.cancel-button {
    background-color: white;
    border: 1px solid #e0e0e0;
    color: #333;
}

.apply-button {
    background-color: #0078ff;
    border: none;
    color: white;
}

.cancel-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.apply-button:hover {
    background-color: #0066dd;
}
</style>
